/**
 * Required marking for admin forms, one rule for every form (Carbon: mark the minority).
 * Most fields required: the optional ones get "(optional)". Otherwise the required ones get "(required)". All or none
 * required: no suffix. Never an asterisk in the label. The input also gets the `required` attribute for assistive
 * technology. Usage: `const marking = requiredMarking(fields)`, then `useMarkedLabel()`;
 * a required field left empty shows `t('admin.validation.required')` once it was left (blur) or the form was submitted.
 */
import { useTranslation } from '@pos/i18n';
import type { ResourceDef } from '../resources';

export type RequiredMarking = 'required' | 'optional' | 'none';

export function requiredMarking(fields: readonly { required?: boolean | null }[]): RequiredMarking {
  const required = fields.filter((f) => f.required).length;
  const optional = fields.length - required;
  if (required === 0 || optional === 0) return 'none';
  return required > optional ? 'optional' : 'required';
}

/**
 * The label with its "(required)" / "(optional)" suffix per the marking. A hook because the suffix is translated:
 * `const label = useMarkedLabel(); label(t('x'), field.required, marking)`.
 */
export function useMarkedLabel() {
  const { t } = useTranslation();
  return (text: string, required: boolean | null | undefined, marking: RequiredMarking = 'required'): string => {
    if (marking === 'required' && required) return `${text} ${t('admin.required')}`;
    if (marking === 'optional' && !required) return `${text} ${t('admin.optional')}`;
    return text;
  };
}

/** Empty for a required field: nothing chosen or typed. */
export const isEmptyValue = (value: unknown): boolean =>
  value == null ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.values(value as Record<string, unknown>).every((v) => isEmptyValue(v)));

/** Required fields (shown as plain inputs) that are still empty; the caller blocks saving while there are any. */
export function missingRequired(def: ResourceDef, values: Record<string, unknown>, hasAddressSource = false) {
  const keys = hasAddressSource ? def.address : undefined;
  const addressKeys = keys
    ? new Set([keys.country, keys.province, keys.district, keys.ward, keys.postal, keys.street])
    : undefined;
  return def.fields.filter(
    (f) =>
      f.required &&
      f.type !== 'toggle' &&
      f.type !== 'weekdays' &&
      !addressKeys?.has(f.key) &&
      isEmptyValue(values[f.key]),
  );
}
